import React, { useEffect, useRef, useState } from "react";
import { XMarkIcon, CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import {
  getContactDisplayName,
  getContactDisplayTitle,
  getContactOptionId,
  normalizeApiContact,
} from "../utils/requestContactDesignations";

const NEW_CONTACT_ID = "new-contact";

/** Request-form contact picker styling — indigo on both VQQ and Assessment (Innovation-Lab). */
const STYLES = {
  chip: "bg-indigo-50 text-indigo-700 ring-indigo-700/10",
  chipTitle: "text-indigo-400",
  chipRemove: "text-indigo-400 hover:text-indigo-600",
  rowSelected: "bg-indigo-50 text-indigo-600",
  rowTitleSelected: "text-indigo-400",
  check: "text-indigo-600",
  focusRing: "focus-within:ring-indigo-600",
  addNewHover: "hover:bg-indigo-50",
};

function ContactNameTitle({ name, title, titleClass, fallbackTitle = "Team Member" }) {
  const role = title?.trim() || fallbackTitle;
  return (
    <>
      {name}{" "}
      <span className={`font-normal ${titleClass}`}>/ {role}</span>
    </>
  );
}

/**
 * Multi-select request contact dropdown filtered by category (VQQ / Assessment).
 */
export default function RequestContactMultiSelect({
  label = "Request Contact(s)",
  placeholder = "Select request contact(s)",
  contacts = [],
  selectedContacts = [],
  onChange,
  addNewContactOption = true,
  addNewContactLabel = "Add a contact that is not listed above",
  onAddNewContact,
  disabled = false,
  isAddingNewContact = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const options = [
    ...contacts.map((c, index) => {
      const normalized = normalizeApiContact(c, index);
      return {
        ...normalized,
        id: getContactOptionId(normalized, index),
        displayName: getContactDisplayName(normalized),
        displayTitle: getContactDisplayTitle(normalized),
      };
    }),
    ...(addNewContactOption
      ? [{ id: NEW_CONTACT_ID, displayName: addNewContactLabel, isNew: true }]
      : []),
  ];

  const listedContacts = options.filter((o) => !o.isNew && o.displayName);
  const visibleSelectedContacts = selectedContacts.filter((c) => getContactDisplayName(c));

  const toggleContact = (contact) => {
    if (contact.isNew) {
      onAddNewContact?.();
      setOpen(false);
      return;
    }
    const exists = selectedContacts.some((s) => s.id === contact.id);
    if (exists) {
      onChange(selectedContacts.filter((s) => s.id !== contact.id));
    } else {
      onChange([...selectedContacts, contact]);
    }
  };

  const removeChip = (e, id) => {
    e.stopPropagation();
    onChange(selectedContacts.filter((s) => s.id !== id));
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
        {label}
      </label>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className={`flex flex-wrap items-center gap-1.5 rounded-md shadow-sm ring-1 ring-inset ring-gray-200 ${STYLES.focusRing} hover:bg-gray-50 transition-colors min-h-[38px] px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {visibleSelectedContacts.length > 0 ? (
          visibleSelectedContacts.map((c) => {
            const name = getContactDisplayName(c);
            const title = getContactDisplayTitle(c);
            return (
              <span
                key={c.id}
                className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STYLES.chip}`}
              >
                <ContactNameTitle
                  name={name}
                  title={title}
                  titleClass={STYLES.chipTitle}
                />
                {c.isCategoryPrimary && (
                  <span className="text-amber-600 text-[9px] font-semibold ml-0.5" aria-hidden>
                    ★
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => removeChip(e, c.id)}
                  className={`ml-0.5 inline-flex items-center ${STYLES.chipRemove}`}
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            );
          })
        ) : isAddingNewContact ? (
          <span className="text-sm text-gray-500">Adding new contact...</span>
        ) : (
          <span className="text-sm text-gray-500">{placeholder}</span>
        )}
        <span className="pointer-events-none ml-auto flex items-center">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </div>

      {open && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {listedContacts.map((opt) => {
            const selected = selectedContacts.some((s) => s.id === opt.id);
            return (
              <li key={opt.id}>
                <button
                  type="button"
                  onClick={() => toggleContact(opt)}
                  className={`relative flex w-full cursor-pointer select-none items-center justify-between py-2 pl-3 pr-9 text-left text-sm ${
                    selected ? STYLES.rowSelected : "text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <span className="block truncate">
                    <ContactNameTitle
                      name={opt.displayName}
                      title={opt.displayTitle}
                      titleClass={
                        selected ? STYLES.rowTitleSelected : "text-gray-500"
                      }
                    />
                    {opt.isCategoryPrimary && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                        Primary
                      </span>
                    )}
                  </span>
                  {selected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                      <CheckIcon className={`h-5 w-5 ${STYLES.check}`} aria-hidden="true" />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
          {addNewContactOption && (
            <li>
              <button
                type="button"
                onClick={() => toggleContact(options.find((o) => o.isNew))}
                className={`relative w-full cursor-pointer select-none py-2 pl-3 pr-9 text-left ${STYLES.addNewHover}${
                  listedContacts.length > 0 ? " border-t border-gray-100" : ""
                }`}
              >
                <span className="block truncate italic text-gray-500">
                  {addNewContactLabel}
                </span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export { NEW_CONTACT_ID };
